import React from "react";
import { motion } from "framer-motion";
import { Check, Clock, ArrowRight } from "lucide-react";

export default function Services({ services, onSelect }) {
  return (
    <section id="services" className="relative bg-[#050505] py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div className="max-w-xl">
            <span className="text-blue-500 uppercase text-xs tracking-[0.3em] font-semibold mb-4 block">
              · 03 Our Packages
            </span>
            <h2 className="font-heading text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light">
              Three signatures.
              <br />
              <span className="font-medium">One commitment.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-base md:text-lg">
            Transparent pricing. No surprises. Every package finished by a certified detailer in our
            mobile studio.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {services.map((svc, idx) => {
            const popular = svc.popular;
            return (
              <motion.div
                key={svc.id || svc.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                data-testid={`service-card-${svc.slug}`}
                className={`relative flex flex-col p-8 md:p-10 transition-all duration-300 ${
                  popular
                    ? "bg-[#0c0e15] border border-blue-500/40 shadow-[0_0_60px_-20px_rgba(59,130,246,0.4)] lg:scale-[1.03]"
                    : "bg-[#0a0a0c] border border-white/8 hover:border-white/20"
                }`}
              >
                {popular && (
                  <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] tracking-[0.25em] uppercase font-semibold px-4 py-2">
                    Most Booked
                  </div>
                )}

                <div className="mb-6">
                  <p className="text-zinc-500 text-xs uppercase tracking-[0.25em] mb-3">
                    Package · 0{idx + 1}
                  </p>
                  <h3 className="font-heading text-white text-3xl md:text-4xl tracking-tight font-medium">
                    {svc.name}
                  </h3>
                  <p className="text-zinc-400 mt-3 text-sm md:text-base">{svc.tagline}</p>
                </div>

                <div className="flex items-end gap-2 mb-8 pb-8 border-b border-white/10">
                  <span className="font-heading text-white text-5xl md:text-6xl font-light tracking-tighter">
                    ${svc.price}
                  </span>
                  <div className="flex items-center gap-1 text-zinc-500 text-sm pb-3">
                    <Clock size={13} />
                    <span>{Math.floor(svc.duration_minutes / 60)}h {svc.duration_minutes % 60 ? `${svc.duration_minutes % 60}m` : ""}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-10 flex-1">
                  {svc.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-zinc-300 text-sm">
                      <Check
                        size={16}
                        className={`mt-0.5 flex-shrink-0 ${popular ? "text-blue-400" : "text-zinc-500"}`}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`select-service-${svc.slug}`}
                  onClick={() => onSelect(svc.slug)}
                  className={`group inline-flex items-center justify-between w-full px-6 py-4 text-sm font-medium tracking-wide transition-colors ${
                    popular
                      ? "bg-blue-600 hover:bg-blue-500 text-white"
                      : "bg-white text-black hover:bg-zinc-200"
                  }`}
                >
                  Book this package
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
